import { Trash01 } from "@untitledui/icons";
import { useMemo, useState } from "react";
import { useCreators } from "@/api/creator";
import { useAddSampleExemptions, useRemoveSampleExemption, useSampleExemptions } from "@/api/sample-rules";
import { Avatar } from "@/components/base/avatar/avatar";
import { Select } from "@/components/base/select/select";
import { useDebounce } from "@/hooks/use-debounce";
import { getCreatorName } from "@/utils/creator-name";

export function ExemptCreatorList() {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);
  const { data: searchResults } = useCreators(debouncedSearch);
  const { data: exemptionsData } = useSampleExemptions();
  const addExemptions = useAddSampleExemptions();
  const removeExemption = useRemoveSampleExemption();

  const exemptions = exemptionsData?.items ?? [];
  const exemptedCreatorIds = useMemo(() => new Set(exemptions.map((e) => e.creatorOpenId)), [exemptions]);

  const options = useMemo(
    () =>
      (searchResults ?? [])
        .filter((c) => !exemptedCreatorIds.has(c.creatorId))
        .map((c) => {
          const { nickname, handle } = getCreatorName(c.nickname, c.creatorName);
          return {
            id: c.creatorId,
            label: nickname ?? handle ?? c.creatorName,
            supportingText: nickname ? (handle ?? undefined) : undefined,
          };
        }),
    [searchResults, exemptedCreatorIds],
  );

  return (
    <div className="flex flex-col gap-3">
      <Select.ComboBox
        size="sm"
        placeholder="Search creators to exempt…"
        items={options}
        inputValue={search}
        onInputChange={setSearch}
        selectedKey={null}
        onSelectionChange={(key) => {
          if (typeof key === "string") {
            addExemptions.mutate(key);
            setSearch("");
          }
        }}
      >
        {(item) => (
          <Select.Item id={item.id} supportingText={item.supportingText}>
            {item.label}
          </Select.Item>
        )}
      </Select.ComboBox>

      {exemptions.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {exemptions.map((exemption) => {
            const { nickname, handle } = getCreatorName(
              exemption.nickname,
              exemption.creatorName ?? exemption.username,
            );
            const displayName = nickname ?? handle;
            return (
              <div
                key={exemption.creatorOpenId}
                className="flex flex-row items-center justify-between gap-3 rounded-lg bg-secondary_alt px-3 py-2"
              >
                <div className="flex min-w-0 flex-row items-center gap-3">
                  <Avatar size="sm" src={exemption.avatar} alt={displayName ?? undefined} />
                  <div className="flex min-w-0 flex-col">
                    {displayName ? (
                      <>
                        <span className="truncate text-xs font-medium text-primary">{displayName}</span>
                        {nickname && handle && <span className="truncate text-xs text-tertiary">{handle}</span>}
                      </>
                    ) : (
                      <span
                        className="truncate font-mono text-xs text-tertiary"
                        title="No profile data synced yet for this creator"
                      >
                        {exemption.creatorOpenId}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  className="shrink-0 p-1 text-quaternary hover:text-primary transition-colors"
                  onClick={() => removeExemption.mutate(exemption.creatorOpenId)}
                  aria-label="Remove exemption"
                >
                  <Trash01 className="size-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
